import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

export interface OneSignalDebugResult {
  success: boolean;
  issues: string[];
  details: {
    platform: string;
    environment: any;
    oneSignalPlugin: any;
    deviceState: any;
    userState: any;
    permissions: any;
    cordova: any;
  };
  recommendations: string[];
}

/**
 * Enhanced OneSignal debugging utility with detailed error analysis
 */
export const runEnhancedOneSignalDebug = async (): Promise<OneSignalDebugResult> => {
  const issues: string[] = [];
  const recommendations: string[] = [];
  const details: any = {
    platform: '',
    environment: {},
    oneSignalPlugin: {},
    deviceState: {},
    userState: {},
    permissions: {},
    cordova: {}
  };

  try {
    // Platform check
    details.platform = Capacitor.getPlatform();
    

    if (!Capacitor.isNativePlatform()) {
      issues.push('Not running on native platform');
      recommendations.push('Deploy to iOS/Android device or simulator');
      return { success: false, issues, details, recommendations };
    }

    // Environment variables
    details.environment = {
      oneSignalAppId: import.meta.env.VITE_ONESIGNAL_APP_ID,
      hasAppId: !!import.meta.env.VITE_ONESIGNAL_APP_ID
    };

    if (!import.meta.env.VITE_ONESIGNAL_APP_ID) {
      issues.push('VITE_ONESIGNAL_APP_ID not configured');
      recommendations.push('Set VITE_ONESIGNAL_APP_ID environment variable');
    }

    // Check window object
    if (typeof window === 'undefined') {
      issues.push('Window object not available');
      return { success: false, issues, details, recommendations };
    }

    // Cordova analysis
    details.cordova = {
      available: !!window.cordova,
      version: window.cordova?.version,
      hasExec: !!(window.cordova && window.cordova.exec)
    };

    // Plugins analysis
    details.oneSignalPlugin = {
      pluginsObjectExists: !!window.plugins,
      availablePlugins: window.plugins ? Object.keys(window.plugins) : [],
      oneSignalExists: !!(window.plugins && window.plugins.OneSignal),
      oneSignalType: window.plugins?.OneSignal ? typeof window.plugins.OneSignal : 'undefined'
    };

    if (!window.plugins) {
      issues.push('window.plugins object not found');
      recommendations.push('OneSignal plugin may not be installed. Run: npm install onesignal-cordova-plugin && npx cap sync');
    }

    if (!window.plugins?.OneSignal) {
      issues.push('OneSignal plugin not found in window.plugins');
      recommendations.push('Check OneSignal plugin installation');
    } else {
      // Analyze OneSignal plugin structure
      const oneSignal = window.plugins.OneSignal;
      details.oneSignalPlugin.methods = Object.getOwnPropertyNames(oneSignal);
      details.oneSignalPlugin.hasUser = !!oneSignal.User;
      details.oneSignalPlugin.hasNotifications = !!oneSignal.Notifications;
      details.oneSignalPlugin.hasGetDeviceState = typeof oneSignal.getDeviceState === 'function';
      details.oneSignalPlugin.hasSetAppId = typeof oneSignal.setAppId === 'function';
      details.oneSignalPlugin.hasInit = typeof oneSignal.init === 'function';
      details.oneSignalPlugin.hasPromptForPermissions = typeof oneSignal.promptForPushNotificationsWithUserResponse === 'function';

      // Determine SDK version
      if (oneSignal.User && oneSignal.Notifications) {
        details.oneSignalPlugin.sdkVersion = '5.x';
      } else if (oneSignal.getDeviceState) {
        details.oneSignalPlugin.sdkVersion = '3.x/4.x';
      } else {
        details.oneSignalPlugin.sdkVersion = 'unknown';
        issues.push('OneSignal plugin structure not recognized');
      }
    }

    // Test OneSignal adapter creation
    try {
      const oneSignalModule = await import('@/integrations/onesignal');
      const getOneSignalAdapter = (oneSignalModule as any).getOneSignalAdapter;
      
      if (getOneSignalAdapter) {
        const adapter = getOneSignalAdapter();
        details.oneSignalPlugin.adapterAvailable = adapter.isAvailable();
        
        if (adapter.isAvailable()) {
          
          
          try {
            const deviceState = await adapter.getDeviceState();
            details.deviceState = {
              success: true,
              userId: deviceState?.userId,
              hasNotificationPermission: deviceState?.hasNotificationPermission,
              isSubscribed: deviceState?.isSubscribed,
              pushToken: deviceState?.pushToken ? 'present' : 'missing',
              fullState: deviceState
            };
            
          } catch (deviceError) {
            details.deviceState = {
              success: false,
              error: {
                message: deviceError instanceof Error ? deviceError.message : 'Unknown error',
                stack: deviceError instanceof Error ? deviceError.stack : undefined,
                type: typeof deviceError,
                details: deviceError,
                stringified: JSON.stringify(deviceError, Object.getOwnPropertyNames(deviceError))
              }
            };
            issues.push(`Failed to get device state: ${deviceError instanceof Error ? deviceError.message : 'Unknown error'}`);
            
          }

          // Test permissions
          try {
            
            if (typeof adapter.promptForPushNotificationsWithUserResponse === 'function') {
              details.permissions.promptMethodExists = true;
            } else {
              details.permissions.promptMethodExists = false;
              issues.push('Permission prompt method not available');
            }
          } catch (permError) {
            details.permissions.testError = permError instanceof Error ? permError.message : 'Unknown error';
          }

        } else {
          issues.push('OneSignal adapter reports as not available');
          recommendations.push('Check OneSignal plugin installation and configuration');
        }
      } else {
        issues.push('Cannot get OneSignal adapter from integration module');
      }
    } catch (moduleError) {
      details.oneSignalPlugin.moduleImportError = moduleError instanceof Error ? moduleError.message : 'Unknown error';
      issues.push('Failed to import OneSignal integration module');
    }

    // Test direct Cordova OneSignal access
    if (window.cordova?.exec && window.plugins?.OneSignal) {
      
      try {
        const cordovaResult = await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Cordova call timeout'));
          }, 10000);

          window.cordova!.exec(
            (result: any) => {
              clearTimeout(timeout);
              resolve(result);
            },
            (error: any) => {
              clearTimeout(timeout);
              reject(error);
            },
            'OneSignalPush',
            'getPermissionSubscriptionState',
            []
          );
        });

        details.cordova.directTestResult = cordovaResult;
        
      } catch (cordovaError) {
        details.cordova.directTestError = {
          message: cordovaError instanceof Error ? cordovaError.message : 'Unknown error',
          type: typeof cordovaError,
          details: cordovaError
        };
        
      }
    }

    // Check user state in database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('onesignal_user_id, notification_enabled, notification_permission_asked')
          .eq('id', user.id)
          .single();

        if (userError) {
          details.userState.databaseError = userError.message;
          issues.push('Failed to get user data from database');
        } else {
          details.userState = {
            hasOneSignalId: !!userData?.onesignal_user_id,
            oneSignalId: userData?.onesignal_user_id,
            notificationEnabled: userData?.notification_enabled,
            permissionAsked: userData?.notification_permission_asked
          };

          if (!userData?.onesignal_user_id) {
            issues.push('User has no OneSignal ID in database');
            recommendations.push('Run OneSignal ID migration or re-register user');
          }
        }
      } else {
        details.userState.authenticated = false;
        issues.push('User not authenticated');
      }
    } catch (dbError) {
      details.userState.error = dbError instanceof Error ? dbError.message : 'Unknown error';
      issues.push('Database connection failed');
    }

    // Generate final recommendations
    if (issues.length === 0) {
      recommendations.push('OneSignal appears to be properly configured');
      recommendations.push('Check device notification settings if notifications still not working');
    } else {
      recommendations.push('Address the issues listed above');
      recommendations.push('Run this debug again after fixes');
    }

    
    
    return {
      success: issues.length === 0,
      issues,
      details,
      recommendations
    };

  } catch (error) {
    
    issues.push(`Debug process failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    
    return {
      success: false,
      issues,
      details,
      recommendations: ['Fix debug process errors and try again']
    };
  }
};

/**
 * Generate a human-readable debug report
 */
export const generateDebugReport = (result: OneSignalDebugResult): string => {
  let report = `
# OneSignal Debug Report
Generated: ${new Date().toISOString()}

## Summary
- Success: ${result.success ? '✅' : '❌'}
- Issues Found: ${result.issues.length}

## Platform & Environment
- Platform: ${result.details.platform}
- OneSignal App ID: ${result.details.environment.hasAppId ? '✅ Configured' : '❌ Missing'}

## Plugin Status
- Plugins Object: ${result.details.oneSignalPlugin.pluginsObjectExists ? '✅' : '❌'}
- OneSignal Plugin: ${result.details.oneSignalPlugin.oneSignalExists ? '✅' : '❌'}
- SDK Version: ${result.details.oneSignalPlugin.sdkVersion || 'Unknown'}
- Adapter Available: ${result.details.oneSignalPlugin.adapterAvailable ? '✅' : '❌'}

## Device State
- Retrieved: ${result.details.deviceState.success ? '✅' : '❌'}
- OneSignal User ID: ${result.details.deviceState.userId || 'None'}
- Has Permission: ${result.details.deviceState.hasNotificationPermission ? '✅' : '❌'}
- Is Subscribed: ${result.details.deviceState.isSubscribed ? '✅' : '❌'}

## User Database State
- Authenticated: ${result.details.userState.authenticated !== false ? '✅' : '❌'}
- Has OneSignal ID: ${result.details.userState.hasOneSignalId ? '✅' : '❌'}
- Notification Enabled: ${result.details.userState.notificationEnabled ? '✅' : '❌'}
- Permission Asked: ${result.details.userState.permissionAsked ? '✅' : '❌'}

## Issues Found
${result.issues.map(issue => `- ${issue}`).join('\n')}

## Recommendations
${result.recommendations.map(rec => `- ${rec}`).join('\n')}

## Technical Details
${JSON.stringify(result.details, null, 2)}
  `;

  return report.trim();
}; 